import React, { useRef, useState } from 'react';
import { Box, CircularProgress, Grid, Pagination, Stack, Tooltip, Typography } from '@mui/material';

import { useTranslation } from 'react-i18next';
import BasicTabs from '../../common/basic-tabs/BasicTabs';
import DetailsTabs from './components/DetailsTab';
import ContactsTabs from './components/ContactsTabs';
import DocumentsTabs from './components/DocumentsTabs';
import NotesTab from '../../common/notes-tab/NotesTab';
import FormUpload from '../../common/form-upload/FormUpload';
import UploadButton from '../../common/upload-button/UploadButton';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import {
  selectBusinessProfileContacts,
  selectBusinessProfileDetails,
} from '../../slices/business-profile/businessProfileSlice';
import styles from './BusinessProfile.module.scss';
import CreateContactModal from '../../common/create-contact-modal/CreateContactModal';
import { selectAllCompanies } from '../../slices/companies/companiesSlice';

import DashboardIcon from '../../../assets/svg/dashboard.svg?react';
import BallotIcon from '../../../assets/svg/ballot.svg?react';
import Button from '@mui/material/Button';
import { useDocuments } from '../document-vault/hooks/useDocuments';
import InfoIcon from '@mui/icons-material/Info';
import PreviewTooltip from '../../common/preview-block/PreviewTooltip';
import Preview from './components/preview-block/Preview';
import { USER_PERMISSIONS } from '../../common/constants/permissions';
import { useCheckPermission } from '../../common/hooks/useCheckPermission';

const BusinessProfile = () => {
  const { t } = useTranslation();
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const company = useAppSelector(selectAllCompanies)[0];
  const businessProfileDetails = useAppSelector(selectBusinessProfileDetails);
  const businessContacts = useAppSelector(selectBusinessProfileContacts);
  const formUploadRef = useRef<HTMLDivElement>(null);
  const uploadButtonRef = useRef<HTMLButtonElement>(null);
  const [workflow, setWorkflow] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const sortByDate = (a: any, b: any) => {
    return Number(new Date(b.created)) - Number(new Date(a.created));
  };
  const {
    documents,
    documentsCount,
    currentPage,
    setCurrentPage,
    getRequestedPage,
    showWorkflowBreadcrumbs,
    setShowWorkflowBreadcrumbs,
    cardView,
    setCardView,
  } = useDocuments('business', workflow);
  const combinedDocuments = [...documents].slice().sort((a, b) => sortByDate(a, b));
  const checkPermission = useCheckPermission();
  //.filter((doc) => doc.category === 'business');

  const handleUploadClose = () => {
    setIsUploadOpen(false);
  };

  // Actually handles both open & close
  const handleUploadOpen = () => {
    setIsUploadOpen((prev) => !prev);
  };

  const toogleShowAddlModal = () => {
    setIsModalOpen((prev) => !prev);
  };

  // Items per page - adjust this value according to your API
  const itemsPerPage = 20;
  // Calculate total pages
  const totalPages = Math.ceil(documentsCount / itemsPerPage);

  // Handle page change
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    getRequestedPage(page);
  };

  if (!company) {
    return <CircularProgress />;
  }

  return (
    <Grid container spacing={3} sx={{ mt: '-78px' }}>
      <Grid size={{ xs: 12 }} sx={{ pt: '16px !important' }}>
        <Stack spacing={0} sx={{ marginBottom: '20px' }}>
          <Typography
            variant='h5'
            component='h1'
            sx={{ width: '50%', mb: '16px' }}
            className={styles.page_title}
          >
            {t('headers.businessProfile')}
          </Typography>
          <Stack direction='row' alignItems={'center'} marginTop={'8px'} spacing={2}>
            <Box ref={uploadButtonRef}>
              {checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_UPLOAD_CREATE) &&
              checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_UPLOAD_MANAGE_BUSINESS_PROFILE) &&
              checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_UPLOAD_BUSINESS_MANAGE) ? (
                <UploadButton
                  onClick={handleUploadOpen}
                  buttonText={t('buttons.uploadFiles')}
                  isArrowDown={false}
                />
              ) : (
                <Tooltip title={t('messages.contactAdministrator')} arrow placement='top'>
                  <UploadButton
                    onClick={handleUploadOpen}
                    buttonText={t('buttons.uploadFiles')}
                    isArrowDown={false}
                    disabled={true}
                  />
                </Tooltip>
              )}
            </Box>
            {isUploadOpen && (
              <Box className={styles.form_box} alignSelf={'flex-start'} ref={formUploadRef}>
                <FormUpload
                  businessContacts={businessContacts}
                  onCancel={handleUploadClose}
                  categoryValue='business'
                  workflow={workflow}
                />
              </Box>
            )}
            <Tooltip
              title={
                checkPermission(USER_PERMISSIONS.ORG_BUSINESS_PROFILE_CREATE_CONTACT)
                  ? t('buttons.createContact')
                  : t('messages.contactAdministrator')
              }
              arrow
              placement='top'
              sx={{ marginRight: 'auto' }}
            >
              <Button
                onClick={toogleShowAddlModal}
                disabled={!checkPermission(USER_PERMISSIONS.ORG_BUSINESS_PROFILE_CREATE_CONTACT)}
                className={styles.add_button}
              >
                {t('buttons.createContact')}
              </Button>
            </Tooltip>
            <PreviewTooltip
              title={<Preview />}
              arrow
              placement='top'
              sx={{ marginRight: 'auto !important' }}
            >
              <InfoIcon color='primary' fontSize='small' />
            </PreviewTooltip>
            <Box display={'flex'} alignItems={'center'} gap={'8px'} marginLeft={'auto !important'}>
              <span style={{ fontSize: '14px' }}>Documents view: </span>
              {documents.length > 0 && (workflow || !cardView) && (
                <Box className='pagination-block' sx={{ display: 'flex', justifyContent: 'end' }}>
                  <Typography className='pagination-pages-value' variant='caption'>
                    Page {currentPage} of {totalPages}
                  </Typography>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    siblingCount={0}
                    boundaryCount={0}
                    // don't enable first/last buttons
                    /*renderItem={(item) => {
                                                              if (item.type === 'previous' || item.type === 'next') {
                                                                return <PaginationItem {...item} />;
                                                              }
                                                              // Render nothing for pages & ellipses so they don't exist in the DOM
                                                              return null;
                                                            }}*/
                    sx={{
                      // optional: tighten arrow spacing
                      '& .MuiPagination-ul': { gap: 0 },
                    }}
                  />
                </Box>
              )}
              <Box className={styles.card_list_view_container}>
                <button
                  className={`${styles.variant_btn} + ${cardView ? styles.variant_btn_active : ''}`}
                  onClick={() => setCardView(true)}
                >
                  <DashboardIcon />
                  <span>{t('buttons.card')}</span>
                </button>
                <button
                  onClick={() => {
                    setCardView(false);
                  }}
                  disabled={!!workflow && showWorkflowBreadcrumbs}
                  className={`${styles.variant_btn} + ${
                    !cardView ? styles.variant_btn_active : ''
                  }`}
                >
                  <BallotIcon />
                  <span>{t('buttons.list')}</span>
                </button>
              </Box>
            </Box>
          </Stack>
        </Stack>
        <Box className={styles.document_header}>
          <Typography variant='h6' component='h2'>
            {businessProfileDetails && company ? company.name : ''}
          </Typography>
        </Box>
      </Grid>
      <Grid size={{ xs: 12 }} className={styles.grid_margin_bottom}>
        <BasicTabs
          tabs={[
            /*t('businessProfile.allTab'),*/
            t('businessProfile.documentsTabs'),
            t('businessProfile.detailsTab'),
            t('businessProfile.contactsTab'),
            t('businessProfile.notesTab'),
          ]}
          tabsChangeSideEffect={() => {
            setWorkflow(null);
          }}
          tabContents={[
            /*<AllTabs company={company} documents={combinedDocuments} key='all' />,*/
            <DocumentsTabs
              company={company}
              documents={combinedDocuments}
              key='documents'
              cardView={cardView}
              workflow={workflow}
              setWorkflow={setWorkflow}
              showWorkflowBreadcrumbs={showWorkflowBreadcrumbs}
              setShowWorkflowBreadcrumbs={setShowWorkflowBreadcrumbs}
            />,
            <DetailsTabs company={company} key='details' />,
            <ContactsTabs companyId={company?.id} key='contacts' />,
            <NotesTab companyId={company?.id} key='notes' />,
          ]}
        />
      </Grid>
      {isModalOpen && (
        <CreateContactModal
          companyId={company?.id}
          open={isModalOpen}
          onClose={() => toogleShowAddlModal()}
        />
      )}
    </Grid>
  );
};

export default BusinessProfile;
